const week7 = {
  number: 7,
  title: "The Navigational Way",
  theme: "How to move when certainty is impossible",
  modules: [
    {
      id: "7.1",
      title: "The Process Method",
      content: `You have learned the architecture of the mind: the Inner House, the residents, the dynamics of friction and leadership.

You have learned the elements of Directional Identity: direction, articulation, anchors, rules.

Now the question becomes: How do you actually move?

The answer is **Process**.

Process is the method by which the Navigational Mind turns uncertainty into forward motion. It is the engine of navigation—the structured way of moving when certainty is impossible.

**Process does not require knowing the destination.** This is its essential difference from goal-oriented approaches. Goals require specificity: exactly what, by exactly when, measured exactly how. Process requires only direction: which way is forward from here.

**Process operates through iteration.** You do not plan the entire journey and then execute the plan. You take a step, learn from the step, adjust, and take another. The path becomes visible only as you walk it.

**Process accumulates certainty.** Remember the null hypothesis: what you don't know will always exceed what you know. But each step deposits a small island of certainty. Not certainty about the whole path—but certainty about what you just learned, what worked, what didn't, what the next step might be.

The Process Method has three components:

1. **The Next True Step** — What is the smallest step that aligns with direction?
2. **The Containment Window** — What boundary will prevent overwhelm?
3. **The Review Moment** — What did this step teach?

These three components form a loop. You take the Next True Step within a Containment Window, then Review. What you learn in the Review informs the next Next True Step.

This is how navigation works. Not grand plans. Not comprehensive strategies. Step, contain, review. Repeat.`
    },
    {
      id: "7.2",
      title: "The Next True Step",
      content: `**The Next True Step (NTS) is the smallest step that aligns with direction, is feasible within current rhythm, and does not trigger internal collapse.**

This definition is precise. Each element matters:

**Smallest** — The NTS is not ambitious. It does not try to do everything at once. It is the minimum viable movement—a step small enough that it can actually be taken.

Why smallest? Because small steps accumulate. Because large steps often fail and teach nothing. Because when capacity is low, even a small step matters.

**Aligns with direction** — The NTS is not random action. It moves you somewhere you are trying to go. Before you can identify the NTS, you must know your direction—or at least sense it enough to distinguish forward from sideways.

**Feasible within current rhythm** — The NTS respects your current capacity. This is crucial. The right step when your Capacity Rhythm is high is not the right step when your Capacity Rhythm is low. The NTS is calibrated to what you can actually do right now.

**Does not trigger internal collapse** — The NTS stays within the window of tolerance. It does not activate the Basement so intensely that the house shakes. It moves you without overwhelming you.

**How to identify the Next True Step:**

1. **Clarify direction** — Even vaguely. Which way is forward?

2. **Assess current capacity** — What is your rhythm right now? What can you actually do?

3. **Identify options** — What steps are possible from here?

4. **Select the smallest aligned step** — Of the options, which is smallest while still moving in the direction?

5. **Test against collapse** — Does thinking about this step activate the Basement? If so, find a smaller step.

**Examples:**

Direction: I want to address a conflict in this relationship.
NTS (high capacity): Have the full conversation.
NTS (medium capacity): Bring up one specific concern.
NTS (low capacity): Text to schedule a time to talk.
NTS (very low capacity): Write out what you want to say, without sending.

In each case, the step aligns with direction. But the size of the step matches the available capacity. All of these are valid Next True Steps—depending on the rhythm.

**The NTS bypasses perfectionism.** Perfectionism says: wait until you can do it right. The NTS says: what is the smallest forward movement possible right now? Movement generates information. Information enables better movement. Waiting generates nothing.`
    },
    {
      id: "7.3",
      title: "The Containment Window",
      content: `**The Containment Window is the boundary that prevents overwhelm—a limit placed on effort or scope.**

Movement without containment becomes flooding. You start the project with enthusiasm and five hours later you have exhausted yourself. You begin addressing one concern and suddenly you are relitigating twenty years of history. You take a small step and it metastasizes into a large step that triggers collapse.

The Containment Window says: This much and no more. It creates a boundary around effort, keeping the step contained enough to complete and learn from.

**Examples of Containment Windows:**

- **Time-based:** "10 minutes." "One hour." "Until the timer rings."
- **Scope-based:** "One paragraph." "One email." "This one issue only."
- **Energy-based:** "Until I notice fatigue." "Until my attention flags."
- **Quantity-based:** "Three items." "One conversation." "The first section only."

**Why containment matters:**

1. **Containment makes starting possible.** Facing an unbounded task activates overwhelm. Facing a ten-minute window does not. Containment lowers the activation threshold.

2. **Containment preserves capacity.** When you work without limits, you often exhaust yourself—and then need days to recover. Contained effort preserves capacity for the next step.

3. **Containment allows completion.** You cannot complete "work on the project" because it has no end point. You can complete "work on the project for 25 minutes." Completion provides feedback and satisfaction.

4. **Containment creates learning opportunities.** Each contained step ends with a natural Review Moment. Without containment, there is no boundary at which to reflect.

**Setting the right Containment Window:**

- **Too small:** Not enough movement to generate data. You stop before anything meaningful happens.
- **Too large:** Movement becomes flooding. You lose control, exhaust yourself, trigger collapse.
- **Right-sized:** Enough movement to learn something, contained enough to maintain stability.

The right size depends on current capacity, the difficulty of the step, and your experience with containment. Start smaller than you think necessary. It is easier to extend a window than to recover from one that was too large.

**Containment is not limitation.** It is the structure that makes sustained movement possible. A river without banks floods and dissipates. A river with banks flows somewhere. Containment creates the conditions for navigation.`
    },
    {
      id: "7.4",
      title: "The Review Moment",
      content: `**The Review Moment is the structured pause after action—the space where experience becomes learning.**

Without review, steps accumulate without meaning. You do things, but you do not learn from them. You move, but you do not navigate. Navigation requires not just action but reflection on action.

**What the Review Moment asks:**

1. **Did this align with my direction?**
Was the step I took actually forward? Or did I drift? Was this the right step, or did I take a different one?

2. **What changed internally?**
What did I feel during the step? What residents activated? What friction occurred? What surprised me about my own response?

3. **What does the mind reveal now?**
Now that I have taken this step, what do I see that I could not see before? What new information is available? What does this step teach me about the next one?

**The Review Moment is not judgment.**

This distinction is essential. Review is not evaluation of whether you did well or badly. It is not criticism of your performance. It is simply noticing—structured attention to what happened and what it means.

Judgment collapses learning. When you approach the Review Moment as an opportunity to criticize yourself, you miss the data. You become defensive. You stop seeing clearly.

The posture of Review is curiosity. "What happened? What do I notice? What can I learn?" This posture allows information to flow.

**Practical Review:**

After completing a Next True Step within a Containment Window, pause and ask:

- What did I actually do?
- What did I feel?
- What worked?
- What didn't?
- What would I do differently?
- What is the Next True Step from here?

This can take two minutes or twenty. The length matters less than the consistency. Review after every contained step creates a learning loop.

**Review is where certainty accumulates.** Remember: certainty is the byproduct of navigating uncertainty. Each Review Moment deposits a small island of certainty—something you now know that you did not know before. Over time, these islands connect. The terrain becomes more visible. Navigation becomes more effective.`
    },
    {
      id: "7.5",
      title: "The Navigation Loop",
      content: `**The Navigation Loop is the mind's self-correcting mechanism—the ongoing cycle through which experience updates orientation.**

The sequence is:

**Sense → Interpret → Act → Reflect → Update → Orient → Repeat**

Let's walk through each stage:

**Sense** — The mind's raw intake. Emotional shifts, bodily cues, environmental changes, internal movements of residents. Before any narration, before any understanding—simply perceiving what is happening.

**Interpret** — Making meaning of what was sensed. Recognizing what the sensations mean, which resident is speaking, what conflict is emerging, what the situation requires.

**Act** — Taking the Next True Step. Moving within a Containment Window. Translating interpretation into action.

**Reflect** — The Review Moment. What happened? What was learned? What changed internally?

**Update** — Incorporating the learning. Refining direction, adjusting orientation, modifying anchors if needed, clarifying rules. This is where the loop actually changes something.

**Orient** — Re-establishing where you are relative to where you are going. After the update, what is the direction now? What is the Next True Step from this new position?

**Repeat** — The loop continues. You sense again, interpret again, act again.

**This is not a linear path; it is a spiral.**

Each cycle does not return you to the same place. Each cycle deepens understanding, deposits certainty, refines direction. You are not going in circles—you are spiraling forward.

**The Navigation Loop is continuous.** It is not something you do occasionally; it is how you move through life. Some loops are conscious and deliberate. Others are automatic and barely noticed. But the structure is always present.

**What breaks the Navigation Loop:**

- **Skipping stages** — Acting without sensing, sensing without interpreting, reflecting without updating. The loop breaks when stages are bypassed.
- **Avoiding stages** — Not wanting to reflect because reflection might be painful. Not wanting to sense because the sensation might be overwhelming. Avoidance creates stagnation.
- **Looping without updating** — Completing cycles but never incorporating the learning. This is unproductive friction—motion without progress.
- **Collapse** — When capacity drops below what the loop requires, the whole system stalls. Collapse is the subject of next week.

**When the Navigation Loop is running well:**

- Movement is continuous
- Learning accumulates
- Direction becomes clearer
- Capacity expands
- The navigator emerges`
    }
  ],
  reflection: {
    prompt: "Identify one thing you've been avoiding because the whole path isn't clear. What would the Next True Step be? What Containment Window would make it possible?",
    placeholder: "Apply the Process Method to something real. Pick the smallest aligned step and define boundaries that would make it actually doable."
  }
};

export default week7;
